import React from "react";
import _ from "lodash";
import Swal from "sweetalert2";

import { OnboardingAPI } from "apis";
import NetworksSelector from "./NetworksSelector";
import YieldSetsSelector from "./YieldSetsSelector";
import Checkbox from "components/common/Checkbox";
import ClickToCopyButton from "components/common/ClickToCopyButton";
import PromptModal from "components/common/PromptModal";
import LoadingUI from "components/common/LoadingUI";
import UnitFullPath from "components/common/UnitFullPath";
import YiedlSetModal from "components/ops-mgmt/account/publishers/gam-accounts/YieldSetModal";
import RecommendUnitsModal from "./onboard-units/RecommendUnitsModal";

import { getQueryStringToObject } from "helpers/QueryString";
import { MESSAGE } from "constants/Message";
import { buttonActionClass } from "helpers/StyleClass";
import { transformCsvToArray } from "helpers/Utils";
import { FiPlusCircle } from "react-icons/fi";
import SimpleTooltip from "components/common/SimpleTooltip";

const RESULT = {
  VALID: "valid",
  INVALID: "invalid",
};

const STATUS = {
  SUCCESS: "Success",
};

class OnboardAdUnits extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      isPromptModalOpen: false,
      isRecommendModalOpen: false,
      isYieldSetModalOpen: false,
      promptHeader: "",
      promptMessage: "",
      promptAction: null,

      queryNetworkId: null,
      queryYieldSetId: null,

      selectedNetwork: null,
      selectedYieldSet: null,

      onboardAdUnitsCsvString: "",
      extUnitIds: [],

      skipParentChildrenOnboardConflictionCheck: false,
      skipEligibleRequestVolumeCheck: false,
      skipEligibleInventoryFormatCheck: false,
      skipSpecialAdUnitCheck: false,
      skipSpecialAdUnitAncestorCheck: false,

      msg: [],
      errMsg: "",
      isSaving: false,

      selectedUnits: [],
      validateUnits: [],
      validateResult: null,
      flag: RESULT.VALID,
      onboardedUnits: [],
    };

    this.onSelectNetwork = this.onSelectNetwork.bind(this);
    this.onSelectYieldSet = this.onSelectYieldSet.bind(this);
    this.onChange = this.onChange.bind(this);

    this.handleSkipCheckChanged = this.handleSkipCheckChanged.bind(this);
    this.handleSkipAll = this.handleSkipAll.bind(this);

    this.handleSelectUnitChanged = this.handleSelectUnitChanged.bind(this);
    this.handleUpdateYieldSet = this.handleUpdateYieldSet.bind(this);

    this.handleValidateClicked = this.handleValidateClicked.bind(this);
    this.handleOnboard = this.handleOnboard.bind(this);
  }

  componentDidMount() {
    const { networkId, yieldSetId } = getQueryStringToObject(
      this.props.location.search
    );

    if (networkId) {
      this.setState({
        queryNetworkId: _.toInteger(networkId),
        isLoading: true,
      });
    }

    if (yieldSetId && networkId) {
      this.setState({
        queryYieldSetId: _.toInteger(yieldSetId),
      });
    }
  }

  onSelectNetwork(network) {
    if (!this.state.queryYieldSetId) {
      this.setState({ isLoading: false });
    }

    if (network) {
      this.setState({
        selectedNetwork: network,
        selectedYieldSet: null,
      });
    }
  }

  onSelectYieldSet(ys) {
    this.setState({ isLoading: false });

    if (ys) {
      this.setState({
        selectedYieldSet: ys,
      });
    }
  }

  onChange(e) {
    this.setState({
      onboardAdUnitsCsvString: e.target.value,
      extUnitIds: transformCsvToArray(e.target.value),
    });
  }

  handleSkipCheckChanged(key) {
    this.setState({
      [key]: !this.state[key],
    });
  }

  handleSkipAll() {
    this.setState({
      skipParentChildrenOnboardConflictionCheck: true,
      skipEligibleRequestVolumeCheck: true,
      skipEligibleInventoryFormatCheck: true,
      skipSpecialAdUnitCheck: true,
      skipSpecialAdUnitAncestorCheck: true,
    });
  }

  handleSelectUnitChanged(id) {
    const { selectedUnits } = this.state;

    if (_.indexOf(selectedUnits, id) === -1) {
      this.setState({
        selectedUnits: [...selectedUnits, id],
      });
    } else {
      this.setState({
        selectedUnits: _.filter(selectedUnits, (unit) => unit !== id),
      });
    }
  }

  handleUpdateYieldSet(data) {
    this.setState({
      selectedYieldSet: data,
    });
  }

  async handleValidateClicked() {
    let saveSwal = Swal;

    try {
      const {
        selectedNetwork,
        selectedYieldSet,
        onboardAdUnitsCsvString,
        extUnitIds,
        skipParentChildrenOnboardConflictionCheck,
        skipEligibleInventoryFormatCheck,
        skipEligibleRequestVolumeCheck,
        skipSpecialAdUnitCheck,
        skipSpecialAdUnitAncestorCheck,
      } = this.state;

      if (!selectedNetwork) {
        throw "Please select a network";
      }

      if (!selectedYieldSet) {
        throw "Please select a Yield Set";
      }

      if (onboardAdUnitsCsvString === "") {
        throw "Please input ext unit ids that you would like to onboard";
      }

      const params = {
        networkId: selectedNetwork.networkId,
        networkCode: selectedNetwork.code,
        extUnitIds,
        skipParentChildrenOnboardConflictionCheck,
        skipEligibleInventoryFormatCheck,
        skipEligibleRequestVolumeCheck,
        skipSpecialAdUnitCheck,
        skipSpecialAdUnitAncestorCheck,
      };

      this.setState({
        onboardedUnits: [],
        selectedUnits: [],
        validateUnits: [],
        validateResult: null,
      });

      this._showSavingPopup(saveSwal, "Validating...");

      const result = await OnboardingAPI.validateOnboardGamAdUnits(params);

      const response = await fetch(result.detailUrl);
      if (!response.ok) {
        throw new Error(
          `Download Fail: ${response.status} ${response.statusText}`
        );
      }

      const text = await response.text();
      const validateUnits = this._parseCsvData(text);

      const unitsList = await OnboardingAPI.getOnboardAdUnitCandidates({
        networkId: selectedNetwork.networkId,
        adUnitIds: validateUnits.map((unit) => unit.id),
        reload: false,
      });

      validateUnits.forEach((item, index) => {
        const unit = _.find(unitsList, { extGamUnitId: item.id });
        if (unit) {
          validateUnits[index] = { ...validateUnits[index], ...unit };
        }
      });

      this.setState({
        selectedUnits: validateUnits
          .filter((unit) => unit.status === STATUS.SUCCESS)
          .map((unit) => unit.id),
        validateUnits,
        validateResult: result,
        errMsg: "",
      });
    } catch (err) {
      this.setState({
        errMsg: err,
      });
    }

    setTimeout(() => {
      // window.location.reload(false);
      // this.props.history.goBack();
      this._hideSavingPopup(saveSwal);
    }, 500);
  }

  async handleOnboard(isPrompt = true) {
    const { selectedUnits, validateUnits } = this.state;

    if (isPrompt) {
      this.setState({
        msg: [],
        isPromptModalOpen: true,
        promptHeader: "Onboard the following ad units?",
        promptMessage: (
          <>
            {selectedUnits.map((id) => {
              const unit = _.find(validateUnits, { id });
              return (
                <div key={id} className="text-blue-600">
                  {unit.id} - {unit.name}
                </div>
              );
            })}
          </>
        ),
        promptAction: () => {
          this.handleOnboard(false);
        },
      });
      return;
    }

    try {
      const {
        selectedNetwork,
        selectedYieldSet,
        onboardAdUnitsCsvString,
        skipParentChildrenOnboardConflictionCheck,
        skipEligibleInventoryFormatCheck,
        skipEligibleRequestVolumeCheck,
        skipSpecialAdUnitCheck,
        skipSpecialAdUnitAncestorCheck,
      } = this.state;
      if (!selectedNetwork) {
        throw "Please select a network";
      }

      if (!selectedYieldSet) {
        throw "Please select a Yield Set";
      }

      if (onboardAdUnitsCsvString === "") {
        throw "Please input ext unit ids that you would like to onboard";
      }

      const params = {
        yieldSetId: selectedYieldSet.yieldSetId,
        extUnitIds: selectedUnits,
        skipParentChildrenOnboardConflictionCheck,
        skipEligibleInventoryFormatCheck,
        skipEligibleRequestVolumeCheck,
        skipSpecialAdUnitCheck,
        skipSpecialAdUnitAncestorCheck,
      };

      this.setState({
        isSaving: true,
      });

      const result = await OnboardingAPI.onboardGamAdUnits(params);

      this.setState({
        isSaving: false,
        errMsg: "",
        msg: [
          <>
            <div>Success Onboard: (Ad Unit Id - Name)</div>
            {result.gamAdUnits.map((unit) => {
              return (
                <div key={unit.adUnitId} className="text-blue-600">
                  {unit.adUnitId} - {unit.name}
                </div>
              );
            })}
          </>,
          MESSAGE.SUCCESS,
        ],
        onboardedUnits: result.gamAdUnits,
      });
    } catch (err) {
      console.log(err);
      this.setState({
        errMsg: err,
        isSaving: false,
        onboardedUnits: null,
        msg: [err, MESSAGE.ERROR],
      });
    }
  }

  _parseCsvData(data) {
    const rows = data.split("\n");
    const headers = rows[0].split(",").map((t) => _.trim(t));
    const result = []; // { id, status }[]

    rows.slice(1).forEach((row) => {
      const values = row.split(",");

      values.forEach((value, index) => {
        if (value !== "") {
          result.push({
            id: value,
            status: headers[index],
            name: "",
            inventoryFormats: [],
          });
        }
      });
    });

    return result;
  }

  _showSavingPopup(saveSwal, title) {
    saveSwal.fire({
      title: title || "Saving...",
      timerProgressBar: true,
      showConfirmButton: true,
      didOpen: () => {
        Swal.showLoading();
      },
      allowOutsideClick: () => !Swal.isLoading(),
      backdrop: true,
    });
  }

  _hideSavingPopup(saveSwal) {
    saveSwal.close();
  }

  render() {
    const {
      isLoading,
      isSaving,
      isPromptModalOpen,
      isRecommendModalOpen,
      isYieldSetModalOpen,
      promptAction,
      promptHeader,
      promptMessage,
      queryNetworkId,
      queryYieldSetId,
      selectedNetwork,
      selectedYieldSet,
      onboardAdUnitsCsvString,
      extUnitIds,
      msg,
      errMsg,
      validateResult,
      onboardedUnits,
      selectedUnits,
      skipParentChildrenOnboardConflictionCheck,
      skipEligibleInventoryFormatCheck,
      skipEligibleRequestVolumeCheck,
      skipSpecialAdUnitCheck,
      skipSpecialAdUnitAncestorCheck,
      flag,
      validateUnits,
    } = this.state;

    const validUnits = validateUnits.filter(
      (unit) => unit.status === STATUS.SUCCESS
    );

    const invalidUnits = validateUnits.filter(
      (unit) => unit.status !== STATUS.SUCCESS
    );

    const showUnits = flag === RESULT.VALID ? validUnits : invalidUnits;

    return (
      <div>
        {isLoading && (
          <div
            className="absolute left-0 top-0 flex h-full w-full items-center justify-center bg-gray-800 opacity-25"
            style={{ zIndex: 101 }}
          >
            <LoadingUI iconOnly={true}></LoadingUI>
          </div>
        )}

        <div className="bg-white px-12">
          <h1 className="pb-4 pt-8 text-4xl font-extrabold text-gray-900">
            Onboard Ad Units
          </h1>
        </div>

        <div className="min-h-screen bg-gray-200 px-12 py-8">
          <div className="flex gap-6">
            <div className="w-1/2">
              <div className="mb-4">
                1. Select Network:
                <div>
                  <NetworksSelector
                    selectedId={queryNetworkId}
                    selectedNetwork={selectedNetwork}
                    handleOnChange={this.onSelectNetwork}
                    handleNoData={() => this.setState({ isLoading: false })}
                  ></NetworksSelector>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center gap-2">
                  2. Select Yield Set:
                  {selectedNetwork && (
                    <SimpleTooltip tooltipText="Create a new Yield Set">
                      <button
                        className="rounded hover:shadow flex items-center bg-gray-100 text-base text-blue-700 hover:text-blue-500"
                        onClick={() =>
                          this.setState({ isYieldSetModalOpen: true })
                        }
                      >
                        <FiPlusCircle></FiPlusCircle>
                      </button>
                    </SimpleTooltip>
                  )}
                </div>
                {selectedNetwork ? (
                  <div className="flex flex-1 items-center gap-2">
                    <div className="w-full">
                      <YieldSetsSelector
                        defaultValue={queryYieldSetId}
                        selectedNetwork={selectedNetwork}
                        selectedYieldSet={selectedYieldSet}
                        networkId={selectedNetwork.networkId}
                        handleOnChange={this.onSelectYieldSet}
                        handleNoData={() => this.setState({ isLoading: false })}
                      ></YieldSetsSelector>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-700">
                    Please select a network first
                  </div>
                )}
              </div>

              <div className="relative mb-4">
                3. Ext Unit Ids (Use <b>comma</b>, <b>whitespace</b> or{" "}
                <b>line break</b> to separate ids){" "}
                <div className="text-sm text-gray-600">ex. 423,235,246</div>
                {selectedNetwork && (
                  <button
                    className={`${buttonActionClass} absolute right-0 top-0`}
                    onClick={() =>
                      this.setState({ isRecommendModalOpen: true })
                    }
                  >
                    Recommend Units
                  </button>
                )}
                <div>
                  <textarea
                    className="border rounded w-full border-gray-400 p-2"
                    value={onboardAdUnitsCsvString}
                    onChange={this.onChange}
                    rows={5}
                  ></textarea>
                  <div className="text-sm leading-none text-gray-600">
                    {extUnitIds.length} units
                  </div>
                </div>
              </div>

              <div className="rounded mb-4 w-full bg-blue-200 p-4">
                <div className="mb-1 flex flex-col gap-1">
                  <label className="flex cursor-pointer items-center">
                    <Checkbox
                      isChecked={skipParentChildrenOnboardConflictionCheck}
                      onChange={() =>
                        this.handleSkipCheckChanged(
                          "skipParentChildrenOnboardConflictionCheck"
                        )
                      }
                    ></Checkbox>
                    <span className="ml-1 align-middle text-sm font-semibold text-gray-700 hover:text-gray-700">
                      Skip parent children onboard confliction check
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-center">
                    <Checkbox
                      isChecked={skipEligibleRequestVolumeCheck}
                      onChange={() =>
                        this.handleSkipCheckChanged(
                          "skipEligibleRequestVolumeCheck"
                        )
                      }
                    ></Checkbox>
                    <span className="ml-1 align-middle text-sm font-semibold text-gray-700 hover:text-gray-700">
                      Skip eligible request volume check
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-center">
                    <Checkbox
                      isChecked={skipEligibleInventoryFormatCheck}
                      onChange={() =>
                        this.handleSkipCheckChanged(
                          "skipEligibleInventoryFormatCheck"
                        )
                      }
                    ></Checkbox>
                    <span className="ml-1 align-middle text-sm font-semibold text-gray-700 hover:text-gray-700">
                      Skip eligible inventory format check
                    </span>
                  </label>

                  <label className="flex cursor-pointer items-center">
                    <Checkbox
                      isChecked={skipSpecialAdUnitCheck}
                      onChange={() =>
                        this.handleSkipCheckChanged("skipSpecialAdUnitCheck")
                      }
                    ></Checkbox>
                    <span className="ml-1 align-middle text-sm font-semibold text-gray-700 hover:text-gray-700">
                      Skip special ad unit check
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-center">
                    <Checkbox
                      isChecked={skipSpecialAdUnitAncestorCheck}
                      onChange={() =>
                        this.handleSkipCheckChanged(
                          "skipSpecialAdUnitAncestorCheck"
                        )
                      }
                    ></Checkbox>
                    <span className="ml-1 align-middle text-sm font-semibold text-gray-700 hover:text-gray-700">
                      Skip special ad unit ancestor check
                    </span>
                  </label>
                </div>

                <button
                  type="button"
                  className="rounded bg-gray-500 px-4 py-1 text-sm font-semibold text-white hover:bg-gray-600"
                  onClick={this.handleSkipAll}
                >
                  Skip all checks
                </button>
              </div>

              <div className="flex w-full justify-end">
                <button
                  type="button"
                  className="rounded bg-teal-500 px-6 py-2 font-semibold text-white hover:bg-teal-600"
                  onClick={this.handleValidateClicked}
                >
                  Validate and review
                </button>
              </div>
            </div>

            <div style={{ width: "35%" }}>
              {onboardedUnits.length === 0 ? (
                <>
                  <div
                    className={`border ${
                      validateResult && "shadow border-blue-400"
                    } rounded mb-4 p-4`}
                    style={{ height: "24.25rem" }}
                  >
                    <div className="mb-2 font-semibold text-gray-700">
                      Validate Results:
                    </div>
                    {validateResult ? (
                      <>
                        <div className="relative flex gap-2">
                          <div
                            className={`border-l border-r border-t rounded cursor-pointer rounded-b-none px-2 py-1 text-sm ${
                              flag === RESULT.VALID
                                ? "border-gray-400 font-semibold text-blue-600"
                                : "border-gray-300"
                            }`}
                            onClick={() =>
                              this.setState({
                                flag: RESULT.VALID,
                              })
                            }
                          >
                            Valid Ad Units (<span>{validUnits.length}</span>)
                          </div>
                          <div
                            className={`border-l border-r border-t rounded cursor-pointer rounded-b-none px-2 py-1 text-sm ${
                              flag === RESULT.INVALID
                                ? "border-gray-400 font-semibold text-blue-600"
                                : "border-gray-300"
                            }`}
                            onClick={() =>
                              this.setState({
                                flag: RESULT.INVALID,
                              })
                            }
                          >
                            Invalid Ad Units (<span>{invalidUnits.length}</span>
                            )
                          </div>
                          <div
                            className="absolute right-0 text-sm"
                            style={{ top: "-1rem" }}
                          >
                            <ClickToCopyButton
                              copyText={showUnits
                                .map((unit) => unit.id)
                                .join(",")}
                              buttonText="Copy Ext Unit Ids"
                            ></ClickToCopyButton>
                          </div>
                        </div>
                        <div
                          className="border divide-y flex w-full flex-col overflow-y-auto border-gray-400"
                          style={{ height: "18rem" }}
                        >
                          {showUnits.map((unit) => (
                            <div
                              className="flex w-full items-center justify-between bg-white p-2"
                              key={unit.id}
                            >
                              <label className="mr-1 flex cursor-pointer items-center gap-4">
                                <Checkbox
                                  isChecked={
                                    _.indexOf(selectedUnits, unit.id) !== -1
                                  }
                                  onChange={() =>
                                    this.handleSelectUnitChanged(unit.id)
                                  }
                                  isDisabled={unit.status !== STATUS.SUCCESS}
                                ></Checkbox>
                                <div className="flex flex-col">
                                  <div className="text-sm text-gray-600">
                                    {unit.id}
                                  </div>
                                  <div className="font-semibold">
                                    {unit.name || "--"}
                                  </div>
                                  {unit.parentPath && (
                                    <UnitFullPath
                                      path={unit.parentPath}
                                      name={unit.name}
                                      customClass="break-all"
                                    />
                                  )}

                                  {unit.status !== STATUS.SUCCESS ? (
                                    <div className="mt-1 w-56 bg-orange-200 px-2 py-1 text-center text-xs font-semibold text-orange-600">
                                      {_.startCase(unit.status)}
                                    </div>
                                  ) : (
                                    <div className="mt-1 flex flex-wrap gap-2">
                                      {unit.inventoryFormats.map((format) => (
                                        <div
                                          className="bg-blue-200 px-2 py-1 text-center text-xs font-semibold text-blue-600"
                                          key={format}
                                        >
                                          {_.startCase(format)}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {unit.notCompatibleReasons &&
                                    unit.notCompatibleReasons.length > 0 && (
                                      <div className="mt-1 w-full bg-red-200 px-2 py-1 text-center text-xs font-semibold text-red-600">
                                        {unit.notCompatibleReasons.join(", ")}
                                      </div>
                                    )}

                                  {unit.hasChildren && (
                                    <div className="mt-1 w-full bg-teal-200 px-2 py-1 text-center text-xs font-semibold text-teal-600">
                                      Has children
                                    </div>
                                  )}

                                  {unit.isOnboarded && (
                                    <div className="mt-1 w-full bg-green-200 px-2 py-1 text-center text-xs font-semibold text-green-600">
                                      Onboarded
                                    </div>
                                  )}
                                </div>
                              </label>
                              <div className="opacity-50 hover:opacity-100">
                                <ClickToCopyButton
                                  copyText={unit.id}
                                  place="left"
                                  tooltipMessage="Copy Ext Unit Id"
                                ></ClickToCopyButton>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-gray-600">
                        Click the validate button to see validate results
                      </div>
                    )}
                  </div>

                  <div className="mb-4 flex justify-end">
                    <button
                      type="button"
                      className={`rounded px-6 py-2 font-semibold text-white ${
                        selectedUnits.length === 0
                          ? "cursor-not-allowed bg-blue-300"
                          : "cursor-pointer bg-blue-500 hover:bg-blue-600"
                      }`}
                      disabled={
                        selectedUnits.length === 0 ||
                        onboardedUnits.length !== 0
                      }
                      onClick={this.handleOnboard}
                    >
                      Onboard selected ad units (
                      <span>{selectedUnits.length}</span>)
                    </button>
                  </div>
                </>
              ) : (
                <div
                  className={`border ${
                    onboardedUnits && "shadow border-blue-400"
                  } rounded mb-4 p-4`}
                >
                  <div className="mb-2 font-semibold text-gray-700">
                    Onboarded Ad Units List: ({onboardedUnits.length} units)
                  </div>
                  {onboardedUnits ? (
                    <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                      <ul>
                        {onboardedUnits.map((unit) => {
                          return (
                            <li key={unit.adUnitId} className="text-gray-900">
                              {unit.adUnitId} - {unit.name}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">
                      Shows the list of units that were onboarded
                    </div>
                  )}
                </div>
              )}

              <div>
                {errMsg !== "" && (
                  <div className="rounded mb-4 bg-red-100 p-4 font-semibold text-red-700">
                    {errMsg}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {isPromptModalOpen && (
          <PromptModal
            msg={msg}
            isLoading={isSaving}
            isOpenModal={isPromptModalOpen}
            handleConfirm={promptAction}
            handleCancel={() => this.setState({ isPromptModalOpen: false })}
            header={promptHeader}
          >
            {promptMessage}
          </PromptModal>
        )}

        {isRecommendModalOpen && (
          <RecommendUnitsModal
            network={selectedNetwork}
            handleCancel={() => this.setState({ isRecommendModalOpen: false })}
            handleConfirm={(selected) => {
              const newStr =
                this.state.onboardAdUnitsCsvString + selected.join(",");
              this.setState({
                isRecommendModalOpen: false,
                onboardAdUnitsCsvString: newStr,
                extUnitIds: transformCsvToArray(newStr),
              });
            }}
          ></RecommendUnitsModal>
        )}

        {isYieldSetModalOpen && (
          <YiedlSetModal
            networkId={selectedNetwork.networkId}
            selectedYieldSet={null}
            onLoading={(isLoading) => this.setState({ isLoading })}
            onCancel={() => this.setState({ isYieldSetModalOpen: false })}
            onClose={(data) => {
              this.setState({ isYieldSetModalOpen: false });
              this.handleUpdateYieldSet(data);
            }}
          ></YiedlSetModal>
        )}
      </div>
    );
  }
}

export default OnboardAdUnits;
